import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { useNavigate } from 'react-router-dom';

import { AuthModal } from '@features/auth';
import { useSpanPaymentLinkLogin } from '@features/reservation/hooks/useSpanPaymentLinkLogin';

import PageFrame from '@shared/components/page/PageFrame';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

import failureIllustration from '@assets/images/reservation/failure-illustration.webp';
import successIllustration from '@assets/images/reservation/success-illustration.webp';

interface Props {
  success?: boolean;
}

/**
 * The end of a payment made through the payment link. It shows nothing of the reservation — the payer may be anybody
 * holding the link — and offers the login that leads to the reservations.
 */
export default function SpanPaymentLinkResultPage({ success = false }: Props) {
  const testId = TestId.root(success ? 'reservation.spanPaymentLinkSuccess' : 'reservation.spanPaymentLinkFailure');
  const navigate = useNavigate();
  const goToReservations = (): void => {
    navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS]);
  };

  // The renter signed in never pays through the link, so any session here belongs to somebody else.
  const { hasSession, showLogin, requestLogin, handleLoginOpenChange } = useSpanPaymentLinkLogin(goToReservations);

  const handleLogin = (): void => {
    requestLogin();
  };

  const status = success ? 'success' : 'failure';

  return (
    <PageFrame
      fullScreen
      testId={testId}
      drawer={showLogin ? <AuthModal onOpenChange={handleLoginOpenChange} /> : undefined}
      forwardButton={{
        label: tr(
          hasSession
            ? 'reservations:span_payment_link.log_in_to_another_account'
            : 'reservations:span_payment_link.result.log_in_to_see'
        ),
        onClick: handleLogin,
      }}
    >
      <div className="flex h-full w-full items-center justify-center p-4 text-center">
        <div className="inline-flex w-80 flex-col items-center justify-start gap-3">
          <img
            src={success ? successIllustration : failureIllustration}
            alt={tr(`reservations:span_payment_link.result.${status}.image_alt`)}
          />
          <div
            className="text-heading justify-start text-2xl leading-8 font-semibold"
            data-testid={testId.append('title')}
          >
            {tr(`reservations:span_payment_link.result.${status}.title`)}
          </div>
          <div className="text-body justify-start text-base leading-6 font-normal">
            {tr(`reservations:span_payment_link.result.${status}.message`)}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}
